/**
 * Container geometry, client-side.
 *
 * ── This is a *preview*, and the server is the authority ────────────────────
 *
 * Every function here answers "would the server accept this placement", and it has
 * to: a drag with no highlight is a drag you have to guess at, and a grid that only
 * learned a placement was illegal after a round trip would snap items back half a
 * second after the player let go. That is exactly the standing `net/predict.ts` has
 * for movement — the client predicts so the gesture feels immediate, the server
 * decides, and where the two disagree the server wins and the next INVENTORY frame
 * redraws.
 *
 * So: nothing here is a rule. `CMD_MOVE_ITEM` re-derives occupancy from the server's
 * own container on every request, and a modified client that skipped these checks
 * gains nothing but a refusal.
 *
 * ── What must stay in step with Go ──────────────────────────────────────────
 *
 * `canPlaceAt` and `firstFit` mirror `Grid.CanPlaceAt` and `Grid.FirstFit` in
 * `internal/game/grid.go`, including **row-major** first-fit order. If they drift, the
 * client draws a green highlight on a cell the server refuses — the
 * `worldCentre`/`tileCentre` failure, in a panel. `game/container.test.ts` and
 * `internal/game/grid_test.go` run the same scenario table for that reason; changing
 * one means changing both.
 *
 * The footprint itself is **never** computed here: it arrives on the wire, because a
 * size derived client-side would be a second source of truth for the number that
 * decides whether two items overlap. See `placementOf` in `net/protocol.ts`.
 */

import { placementOf, type ContainerPayload, type InventoryItem } from '../net/protocol';

/** A rectangle of cells: an item's footprint at a position. */
export interface Rect {
  col: number;
  row: number;
  width: number;
  height: number;
}

/** Where in a container an item sits, as a rectangle. */
export function rectOf(item: InventoryItem): Rect {
  return placementOf(item);
}

/** True when two rectangles share at least one cell. */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.col < b.col + b.width &&
    a.col + a.width > b.col &&
    a.row < b.row + b.height &&
    a.row + a.height > b.row
  );
}

/** True when a rectangle covers a cell. */
export function covers(r: Rect, col: number, row: number): boolean {
  return col >= r.col && col < r.col + r.width && row >= r.row && row < r.row + r.height;
}

/**
 * Whether a footprint fits at an exact cell.
 *
 * `ignoreUUID` is the item being dragged, excluded from the overlap test — an item
 * always overlaps itself, so without this every nudge and every cancelled drag would
 * highlight red. Mirrors `Grid.CanPlaceAt`.
 */
export function canPlaceAt(
  container: ContainerPayload,
  size: { width: number; height: number },
  col: number,
  row: number,
  ignoreUUID?: string,
): boolean {
  if (size.width < 1 || size.height < 1) return false;
  if (col < 0 || row < 0) return false;
  if (col + size.width > container.cols || row + size.height > container.rows) return false;

  const target: Rect = { col, row, width: size.width, height: size.height };
  for (const other of container.items) {
    if (other.uuid === ignoreUUID) continue;
    if (rectsOverlap(target, rectOf(other))) return false;
  }
  return true;
}

/**
 * The first cell a footprint fits, scanning **row by row from the top left**.
 *
 * The order is the design and not an implementation detail — it is what makes a
 * picked-up item land where the player is looking and a bag fill from one end, so the
 * free space stays in one piece. Mirrors `Grid.FirstFit` exactly; a different order
 * here would predict a different landing cell from the one the server chooses.
 */
export function firstFit(
  container: ContainerPayload,
  size: { width: number; height: number },
  ignoreUUID?: string,
): { col: number; row: number } | null {
  if (size.width < 1 || size.height < 1) return null;
  for (let row = 0; row + size.height <= container.rows; row++) {
    for (let col = 0; col + size.width <= container.cols; col++) {
      if (canPlaceAt(container, size, col, row, ignoreUUID)) return { col, row };
    }
  }
  return null;
}

/** True when a footprint would go in anywhere. */
export function fits(
  container: ContainerPayload,
  size: { width: number; height: number },
  ignoreUUID?: string,
): boolean {
  return firstFit(container, size, ignoreUUID) !== null;
}

/** The item covering a cell, or undefined. */
export function itemAt(
  container: ContainerPayload,
  col: number,
  row: number,
): InventoryItem | undefined {
  return container.items.find((it) => covers(rectOf(it), col, row));
}

/** How many cells are occupied. */
export function usedCells(container: ContainerPayload): number {
  let used = 0;
  for (const it of container.items) {
    const r = rectOf(it);
    used += r.width * r.height;
  }
  return used;
}

/** How many cells are free. Never negative, even against a malformed frame. */
export function freeCells(container: ContainerPayload): number {
  return Math.max(0, container.cols * container.rows - usedCells(container));
}

/**
 * The top-left cell an item would land on, given the cell under the cursor and which
 * cell of the item the player grabbed.
 *
 * **This is what makes a big item feel grabbed rather than pointed at.** Dropping a
 * 2×4 greatsword places its *top-left* corner, so a naive handler puts the whole thing
 * down and to the right of the cursor — the player aims at a hole and the sword lands
 * somewhere else. Subtracting the grab offset means the cell you picked up stays under
 * the pointer for the whole drag.
 *
 * Deliberately **not** clamped into the container. A drop half off the edge has to be
 * *refused*, not quietly slid into a legal position: sliding would place the item
 * somewhere the player did not aim, which is worse than nothing happening.
 */
export function dropOrigin(
  hovered: { col: number; row: number },
  grab: { col: number; row: number },
): { col: number; row: number } {
  return { col: hovered.col - grab.col, row: hovered.row - grab.row };
}

/**
 * Which cell of an item a pointer landed on, from the item's own rectangle.
 *
 * Clamped into the footprint: a pointer event on the 1px border between two cells can
 * round outside, and a grab offset outside the item would push every subsequent drop
 * one cell astray.
 */
export function grabOffset(
  item: InventoryItem,
  cell: { col: number; row: number },
): { col: number; row: number } {
  const r = rectOf(item);
  return {
    col: Math.min(Math.max(cell.col - r.col, 0), r.width - 1),
    row: Math.min(Math.max(cell.row - r.row, 0), r.height - 1),
  };
}
